import React, { useState, useEffect } from 'react';
import QRCode from 'qrcode';
import { generatePromptPayQRData, validatePromptPayNumber } from '../../utils/qrCodeUtils';

/**
 * Component สำหรับแสดงข้อมูลการชำระเงิน
 * รองรับ payment structure ใหม่: หลายบัญชี, หลาย QR Code, จ่ายสด, เก็บปลายทาง
 */
const PaymentMethodsDisplay = ({ payment, productPrice = 0 }) => {
  const [generatedQRCodes, setGeneratedQRCodes] = useState({});
  const [qrCodeErrors, setQrCodeErrors] = useState({});

  // Generate QR Code สำหรับ type='generate'
  useEffect(() => {
    const generateQRCodes = async () => {
      if (!payment?.qrCodes || payment.qrCodes.length === 0) {
        setGeneratedQRCodes({});
        setQrCodeErrors({});
        return;
      }

      const newGeneratedQRCodes = {};
      const newQrCodeErrors = {};
      
      for (let i = 0; i < payment.qrCodes.length; i++) {
        const qr = payment.qrCodes[i];
        
        // Only process generate type QR codes
        if (qr.type === 'generate') {
          if (!qr.promptPayNumber) {
            newQrCodeErrors[i] = 'กรุณากรอก PromptPay Number';
            continue;
          }
          
          try {
            // Validate PromptPay number
            const validation = validatePromptPayNumber(qr.promptPayNumber);
            
            if (!validation.isValid) {
              newQrCodeErrors[i] = validation.error;
              continue;
            }
            
            // Clear error if validation passes
            delete newQrCodeErrors[i];
            
            const amount = qr.amount || productPrice;
            
            // Generate QR Code data with CRC
            const qrData = generatePromptPayQRData(qr.promptPayNumber, amount);
            
            // Generate QR Code image
            const qrCodeDataURL = await QRCode.toDataURL(qrData, {
              width: 400,
              margin: 2,
              color: {
                dark: '#000000',
                light: '#FFFFFF'
              }
            });
            
            newGeneratedQRCodes[i] = qrCodeDataURL;
            console.log(`QR Code generated successfully for index ${i}`);
          } catch (error) {
            console.error('Error generating QR code:', error);
            newQrCodeErrors[i] = error.message || 'เกิดข้อผิดพลาดในการสร้าง QR Code';
          }
        }
      }
      
      setGeneratedQRCodes(newGeneratedQRCodes);
      setQrCodeErrors(newQrCodeErrors);
    };

    generateQRCodes();
  }, [payment?.qrCodes, productPrice]);
  if (!payment || !payment.methods || payment.methods.length === 0) {
    return null;
  }

  return (
    <div className="mb-4">
      <h4 className="text-sm font-semibold text-gray-700 mb-3">💳 วิธีการชำระเงิน:</h4>
      <div className="space-y-4">
        {/* โอนบัญชี */}
        {payment.methods.includes('bank_transfer') && payment.bankAccounts && payment.bankAccounts.length > 0 && (
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-lg">🏦</span>
              <span className="font-medium text-gray-700">โอนเงินผ่านธนาคาร</span>
            </div>
            <div className="space-y-3">
              {payment.bankAccounts.map((account, index) => (
                <div key={index} className="p-3 bg-gray-50 rounded border border-gray-200">
                  <div className="text-xs text-gray-500 mb-1">บัญชี #{index + 1}</div>
                  {account.bank && (
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-gray-600">ธนาคาร:</span>
                      <span className="text-sm text-gray-700">{account.bank}</span>
                    </div>
                  )}
                  {account.accountNumber && (
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-gray-600">เลขบัญชี:</span>
                      <span className="text-sm font-mono text-gray-700">{account.accountNumber}</span>
                    </div>
                  )}
                  {account.accountName && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-600">ชื่อบัญชี:</span>
                      <span className="text-sm text-gray-700">{account.accountName}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* QR Code PromptPay */}
        {payment.methods.includes('qr_code') && payment.qrCodes && payment.qrCodes.length > 0 && (
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-lg">📱</span>
              <span className="font-medium text-gray-700">QR Code PromptPay</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {payment.qrCodes.map((qr, index) => {
                const qrType = qr.type || 'upload';
                const isGenerate = qrType === 'generate';
                const qrCodeImage = isGenerate ? generatedQRCodes[index] : qr.qrCode;
                const displayAmount = qr.amount || productPrice;
                
                return (
                  <div key={index} className="p-3 bg-gray-50 rounded border border-gray-200">
                    {qr.description && (
                      <div className="text-xs text-gray-500 mb-2">{qr.description}</div>
                    )}
                    {isGenerate && (
                      <div className="mb-2">
                        <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">Generate</span>
                      </div>
                    )}
                    {qrCodeImage && (
                      <div className="flex flex-col items-center gap-2">
                        <img
                          src={qrCodeImage}
                          alt={`QR Code ${index + 1}`}
                          className="w-40 h-40 object-contain border-2 border-gray-300 rounded-lg bg-white p-2"
                        />
                        {displayAmount > 0 && (
                          <div className="text-base font-bold text-green-600">
                            ฿{parseFloat(displayAmount).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                        )}
                        <p className="text-xs text-center text-gray-500">
                          {isGenerate ? 'สแกน QR Code เพื่อชำระเงิน (ยอดเงินแสดงอัตโนมัติ)' : 'สแกน QR Code เพื่อชำระเงิน'}
                        </p>
                        {isGenerate && qr.promptPayNumber && (
                          <p className="text-xs text-center text-gray-400">
                            PromptPay: {qr.promptPayNumber}
                          </p>
                        )}
                      </div>
                    )}
                    {/* Loading state for generate type */}
                    {isGenerate && !qrCodeImage && !qrCodeErrors[index] && qr.promptPayNumber && (
                      <div className="flex flex-col items-center gap-2 p-4">
                        <div className="text-sm text-gray-500">กำลังสร้าง QR Code...</div>
                      </div>
                    )}
                    {/* Error state for generate type */}
                    {isGenerate && qrCodeErrors[index] && (
                      <div className="flex flex-col items-center gap-2 p-4">
                        <div className="text-sm text-red-500 text-center">{qrCodeErrors[index]}</div>
                      </div>
                    )}
                    {/* Missing promptPayNumber for generate type */}
                    {isGenerate && !qr.promptPayNumber && (
                      <div className="flex flex-col items-center gap-2 p-4">
                        <div className="text-sm text-red-500">กรุณากรอก PromptPay Number</div>
                      </div>
                    )}
                    {/* Missing QR Code for upload type */}
                    {!isGenerate && !qrCodeImage && (
                      <div className="flex flex-col items-center gap-2 p-4">
                        <div className="text-sm text-red-500">ยังไม่มี QR Code กรุณาอัพโหลดรูป QR Code</div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* จ่ายสด นัดรับ */}
        {payment.methods.includes('cash_on_pickup') && (
          <div className="bg-white p-4 rounded-lg border border-green-200 bg-green-50">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">💰</span>
              <span className="font-medium text-green-700">จ่ายสด นัดรับ</span>
            </div>
            {payment.cashOnPickup?.location && (
              <div className="text-sm text-gray-700 mb-1">
                <span className="font-medium">ที่อยู่:</span> {payment.cashOnPickup.location}
              </div>
            )}
            {payment.cashOnPickup?.time && (
              <div className="text-sm text-gray-700">
                <span className="font-medium">เวลา:</span> {payment.cashOnPickup.time}
              </div>
            )}
            <div className="mt-2 text-sm font-semibold text-green-600">ฟรี</div>
          </div>
        )}

        {/* เก็บปลายทาง */}
        {payment.methods.includes('cash_on_delivery') && (
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">📦</span>
              <span className="font-medium text-gray-700">เก็บปลายทาง (COD)</span>
            </div>
            {payment.cashOnDelivery?.courierCompanies && payment.cashOnDelivery.courierCompanies.length > 0 && (
              <div className="text-sm text-gray-700">
                <span className="font-medium">บริษัทขนส่ง:</span> {payment.cashOnDelivery.courierCompanies.join(', ')}
              </div>
            )}
            <div className="mt-2 text-xs text-gray-500">
              💡 ชำระเงินเมื่อได้รับสินค้า
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentMethodsDisplay;



